import type { ReactNode } from 'react';
import { GraduationCap, Inbox, PackageOpen, Target, TrendingUp, Users } from 'lucide-react';
import { cn } from '@/lib/utils';

type EmptyKind = 'formations' | 'sessions' | 'formateurs' | 'opportunites' | 'finances' | 'generic';

const ICONS: Record<EmptyKind, typeof Inbox> = {
  formations: GraduationCap,
  sessions: PackageOpen,
  formateurs: Users,
  opportunites: Target,
  finances: TrendingUp,
  generic: Inbox,
};

const HUE: Record<EmptyKind, string> = {
  formations: 'from-amber-500/15 to-orange-500/10 text-amber-700 dark:text-amber-300',
  sessions: 'from-violet-500/15 to-fuchsia-500/10 text-violet-700 dark:text-violet-300',
  formateurs: 'from-cyan-500/15 to-teal-500/10 text-cyan-700 dark:text-cyan-300',
  opportunites: 'from-indigo-500/15 to-violet-500/10 text-indigo-700 dark:text-indigo-300',
  finances: 'from-emerald-500/15 to-teal-500/10 text-emerald-700 dark:text-emerald-300',
  generic: 'from-slate-500/10 to-slate-400/5 text-slate-600 dark:text-slate-300',
};

export interface EmptyStateIllustrationProps {
  type?: EmptyKind;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}

export function EmptyStateIllustration({
  type = 'generic',
  title,
  description,
  action,
  className,
}: EmptyStateIllustrationProps): JSX.Element {
  const Icon = ICONS[type];
  return (
    <div
      className={cn(
        'mx-auto flex max-w-lg flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border/60 bg-card/40 px-6 py-10 text-center',
        className,
      )}
    >
      <div
        className={cn(
          'flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ring-1 ring-border/50',
          HUE[type],
        )}
      >
        <Icon className="h-7 w-7" />
      </div>
      <div>
        <div className="text-sm font-semibold">{title}</div>
        {description && (
          <p className="mt-1 max-w-sm text-xs text-muted-foreground">{description}</p>
        )}
      </div>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
